<script setup>

import ScrollPanel from "primevue/scrollpanel";
import UiTimeslots from "../ui-components/UiTimeslots.vue";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import UiCalendar from "../ui-components/UiCalendar.vue";
import InputMask from "primevue/inputmask";
import FormInput from "../ui-components/FormInput.vue";
import LocationBlock from "../ui-components/LocationBlock.vue";
import {computed} from "vue";
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css';
import LocationBlockSingular from "../ui-components/LocationBlockSingular.vue";

const props = defineProps({
    events: Array,
    locations: Array,
    dateTimeRestrictions: Object,
    theme: Object,
    forms: Object,
    currentBrightness: Number,
    currentTimeSlot: Object,
    noForm: Boolean
})

const selectedDay = defineModel('selectedDay');
const form = defineModel('form');
const selectedTimeSlot = defineModel('selectedTimeSlot');

const customizationClasses = computed(() => {
    return [props.theme.roundness < 3 ? 'border-b':'', props.theme.darkMode ? 'border-neutral-700' : '', props.currentBrightness < 140 ? 'text-white': 'text-black'];
})

const containerClasses = computed(() => {
    return {
        'rounded-xl': props.theme.roundness === 1,
        'rounded-2xl': props.theme.roundness === 2,
        'rounded-3xl': props.theme.roundness === 3,
        'bg-transparent border-neutral-700': props.theme.darkMode
    };
})

const needsPhone = computed(() => {
    const ids = props.locations.filter((item) => item.service === 'phone_call' || item.service === 'whatsapp').map(item => item.id);

    return ids.includes(form.value.id_reservation_locations);
})
</script>

<template>
    <section id="date-time" class="flex gap-6 max-md:flex-col justify-center max-md:items-center px-4 lg:px-16">
        <div>
            <div class="flex items-baseline dark:border-neutral-700 mb-3 gap-2 text-lg pb-2" :class="customizationClasses">
                <i class="pi pi-calendar text-gray-400" style="font-size: 1.25rem"></i>
                <!-- Datum -->
                {{$t('widget.date')}}
            </div>
            <UiCalendar
                v-model="selectedDay" :events="events"
                :date-time-restrictions="dateTimeRestrictions"
                :theme="theme" :locale="forms.locale"
                :settings="forms.settings"></UiCalendar>

            <small v-if="$page.props.errors.day" id="timeslot-error" class="text-red-500">{{$page.props.errors.day}}</small>
        </div>
        <div class="grow max-md:w-full">
            <div class="flex items-baseline dark:border-neutral-700 mb-3 gap-2 text-lg pb-2" :class="customizationClasses">
                <i class="pi pi-clock text-gray-400" style="font-size: 1.25rem"></i>
                <!-- Čas -->
                {{$t('widget.time')}}
            </div>
            <ScrollPanel style="height: 270px"
                         :class="containerClasses"
                         class="border grow bg-neutral-50 dark:bg-transparent dark:border-neutral-700">
                <UiTimeslots v-model="selectedTimeSlot" :events="events" :theme="theme" :color="theme.color" :selected-date="selectedDay" :date-time-restrictions="dateTimeRestrictions" :time-slots="currentTimeSlot"></UiTimeslots>
            </ScrollPanel>
            <small v-if="$page.props.errors.timeslot" id="timeslot-error" class="text-red-500">{{$page.props.errors.timeslot}}</small>
        </div>
    </section>
    <section v-if="!noForm && locations && locations.length" id="location" class="px-4 lg:px-16 mb-10">
        <div class="flex items-baseline mt-5 gap-2 text-lg pb-2"
             :class="customizationClasses">
            <i class="pi pi-map-marker text-gray-400" style="font-size: 1.25rem"></i>
            <!-- Formulář -->
            {{$t('widget.location')}}
        </div>

        <div class="mt-4">
            <swiper v-if="locations.length > 1" :space-between="8" :breakpoints="{
                1024: {
                    slidesPerView: locations.length > 3 ? 3.25 : 3
                },
                768: {
                    slidesPerView: locations.length > 3 ? 3.25 : 3
                },
                640: {
                    slidesPerView: locations.length > 1 ? 1.25 : 1
                },
                320: {
                    slidesPerView: locations.length > 1 ? 1.25 : 1,
                    spaceBetween: 4
                }
                }">
                <swiper-slide v-for="location in locations" :key="location.id">
                    <location-block v-model="form.id_reservation_locations" :location="location" :theme="theme" :theming="containerClasses"></location-block>
                </swiper-slide>
            </swiper>
            <location-block-singular v-else-if="locations.length > 0" v-model="form.id_reservation_locations" :location="locations[0]" :theme="theme" :theming="containerClasses"></location-block-singular>
        </div>

        <small v-if="$page.props.errors.id_reservation_locations" id="timeslot-error" class="text-red-500">{{$page.props.errors.id_reservation_locations}}</small>
    </section>
    <section v-if="!noForm " id="form" class="px-4 lg:px-16">
        <div class="flex items-baseline mt-5 gap-2 text-lg pb-2"
             :class="customizationClasses">
            <i class="pi pi-info text-gray-400" style="font-size: 1.25rem"></i>
            <!-- Formulář -->
            {{$t('widget.form')}}
        </div>
        <div class="flex justify-center gap-6 mt-4 dark">
            <div class="grid grid-cols-2 gap-y-2 gap-x-4 grow">
                <!-- Jméno a příjmení -->
                <form-input :label="$t('widget.name_label')" v-model="form.name" :invalid="$page.props.errors.name" required type="text" :text-color="currentBrightness < 140 ? 'text-white': 'text-black'"></form-input>
                <!-- Poznámka -->
                <form-input :label="$t('widget.note_label')" class="row-span-2" v-model="form.note"  rows="4" type="textarea" :text-color="currentBrightness < 140 ? 'text-white': 'text-black'"></form-input>
                <!-- Email -->
                <form-input :label="$t('widget.email_label')" required v-model="form.email" :invalid="$page.props.errors.email" type="text" :text-color="currentBrightness < 140 ? 'text-white': 'text-black'"></form-input>
                <!-- Telefonní číslo -->

                <form-input :label="$t('widget.phone_label')" :required="needsPhone" v-model="form.phone" :invalid="$page.props.errors.phone" type="phone" :text-color="currentBrightness < 140 ? 'text-white': 'text-black'"></form-input>
                <form-input v-for="input in $page.props.formBuilder" :key="input" :type="input.type" :options="input.options"
                            :label="input.label" :required="input.required" v-model="form.formdata[input.id]" :invalid="$page.props.errors['formdata.' + input.id]"
                            :text-color="currentBrightness < 140 ? 'text-white': 'text-black'"></form-input>
            </div>
        </div>
    </section>
</template>

<style scoped lang="scss">

</style>
